const STORAGE_KEY = 'pendingBankConsentResume';
const MAX_AGE_MS = 15 * 60 * 1000;

export interface PendingConsentResume {
  bic: string;
  /** Provider-native ref (FIN-592) — required to resume a bic-less bank, whose bic is the
   *  empty-string sentinel (FIN-783). */
  ref?: string;
  countryCode?: string;
  consentId: string;
  authorisationId: string;
  savedAt: number;
  /** FIN-1567: what the user picked before the same-tab redirect wiped React state -- carried
   *  across so the resumed connectAccount call uses the right login, not the server default. */
  accountType?: 'business' | 'personal';
}

export function savePendingConsentResume(data: Omit<PendingConsentResume, 'savedAt'>): void {
  const entry: PendingConsentResume = { ...data, savedAt: Date.now() };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
}

// Reads and clears the pending entry in one step — the same-tab redirect flow
// resumes at most once per bank sign-in round trip.
export function consumePendingConsentResume(): PendingConsentResume | null {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  sessionStorage.removeItem(STORAGE_KEY);
  if (!raw) return null;

  let parsed: Partial<PendingConsentResume>;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  // `typeof bic === 'string'`, not `!parsed.bic` (FIN-783): a bic-less bank's bic is the
  // legitimate empty-string sentinel, which the old truthiness test silently refused. The real
  // requirement -- named by something -- is that a bic or a ref is present, mirroring
  // requireBankIdentity()'s own guard on the backend.
  if (
    typeof parsed.bic !== 'string' ||
    (!parsed.bic && !parsed.ref) ||
    !parsed.consentId ||
    !parsed.authorisationId ||
    typeof parsed.savedAt !== 'number'
  ) {
    return null;
  }
  if (Date.now() - parsed.savedAt > MAX_AGE_MS) {
    return null;
  }

  return parsed as PendingConsentResume;
}
